import {
  useRouter,
  type RouteLocationAsPathGeneric,
  type RouteLocationAsRelativeGeneric,
} from 'vue-router'
import usePost from '../../../core/composables/api/usePost'
import { useLocalFeedbacks } from '../../../core/composables/useLocalFeedbacks'
import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
import { useSnackStore } from '../../../stores/snack.store'
import type CreatableFeedback from '../models/creatable-feedback'
import { useI18n } from 'vue-i18n'
import useGet from '../../../core/composables/api/useGet'
import type { Member } from '../../../core/models/member/member'
import type { QuestionGroup } from '../models/question-group'
import { nextTick, onMounted, provide, type InjectionKey, type Ref } from 'vue'
import { useEventBus, useLocalStorage } from '@vueuse/core'
import type { ShareType } from '../../../core/models/share-types'
import type { Question } from '@/core/models/question'
import { useWysiwygPlainText } from '@/core/composables/use-wysiwyg-plain-text'

export const useGiveStructuredFeedback = (surveyId: string, receiverId: string) => {
  const { t } = useI18n()
  const router = useRouter()
  const snackStore = useSnackStore()
  const { hasTextContent } = useWysiwygPlainText()

  const {
    data,
    status: loadingState,
    load,
  } = useGet<GiveFeedbackPage>('feedback/structured/' + surveyId + '/receiver/' + receiverId)

  onMounted(async () => {
    await load()
  })

  const state = useLocalStorage<'start' | 'feedback' | 'finished' | 'saved'>(
    'state-' + surveyId + '-' + receiverId,
    'feedback',
  )

  const bus = useEventBus<{
    feedback: CreatableFeedback
    question: Question
    questionGroup: QuestionGroup
  }>('feedbacks-' + surveyId + '-' + receiverId)

  const onFeedbackUpdated = bus.on

  const updateFeedback = (newFeedback: Omit<CreatableFeedback, 'feedbackReceiverId'>) => {
    const existingFeedback = feedbacks.value.find(
      (feedback) =>
        feedback.questionId === newFeedback.questionId &&
        feedback.questionGroupId === newFeedback.questionGroupId,
    )

    const creatableFeedback = {
      ...existingFeedback,
      ...newFeedback,
      feedbackReceiverId: receiverId,
    }

    feedbacks.value = [
      ...feedbacks.value.filter(
        (feedback) =>
          feedback.questionId != newFeedback.questionId ||
          feedback.questionGroupId != newFeedback.questionGroupId,
      ),
    ]

    if (
      hasTextContent(creatableFeedback.text) ||
      creatableFeedback.value !== undefined ||
      (creatableFeedback.subFeedbacks?.length ?? 0) > 0
    ) {
      feedbacks.value.push(creatableFeedback)
    }

    const questionGroup = data.value?.questionGroups.find(
      (questionGroup) => questionGroup.id == newFeedback.questionGroupId,
    )

    const question = questionGroup?.questions.find(
      (question) => question.id == newFeedback.questionId,
    )

    if (questionGroup && question) {
      bus.emit({ feedback: creatableFeedback, question: question, questionGroup: questionGroup })
    }
  }

  const { mutate: saveFeedbacks, status: saveFeedbackStatus } = usePost<
    string,
    {
      feedbacks: CreatableFeedback[]
    }
  >('feedback/structured/' + surveyId + '/receiver/' + receiverId)

  const { feedbacksBySurveyIdAndReceiverId } = useLocalFeedbacks()
  const feedbacks = feedbacksBySurveyIdAndReceiverId(surveyId, receiverId)

  const save = async (
    route?: string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric,
  ) => {
    await saveFeedbacks({
      feedbacks: feedbacks.value,
    })

    switch (saveFeedbackStatus.value) {
      case ApiRequestStatus.Success:
        if (route) {
          router.push(route)
        } else {
          router.back()
        }
        snackStore.showInfo(t('feedback.giveFeedbackSuccess'))
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('feedback.giveFeedbackError'))
        break
    }
  }

  const navigateToQuestionByIdBus = useEventBus<{
    question: { questionId: string; questionGroupId: string }
  }>('navigateToQuestionById-' + surveyId + '-' + receiverId)

  const navigateToQuestionById = (question: { questionId: string; questionGroupId: string }) => {
    // SingleQuestionsView is unmounted while state==='finished' (v-else), so emit after it remounts
    state.value = 'feedback'
    nextTick(() => {
      navigateToQuestionByIdBus.emit({ question })
    })
  }

  const onNavigateToQuestionById = navigateToQuestionByIdBus.on

  provide(giveFeedbackKey, {
    onFeedbackUpdated,
    save,
    updateFeedback,
    feedbacks,
    state,
    navigateToQuestionById,
    onNavigateToQuestionById,
  })

  return { data, loadingState, state }
}

interface GiveFeedbackPage {
  readonly id: string
  readonly title: string
  readonly message: JSON
  readonly isFinished: boolean
  readonly receiver: Member
  readonly sender: Member
  readonly isSelfOnly: boolean
  readonly isSuperpower: boolean
  readonly questionGroups: ReadonlyArray<QuestionGroup>
  readonly supervisors?: ReadonlyArray<Member>
  readonly sharedFeedback?: {
    readonly type: ShareType
    readonly members: ReadonlyArray<Member>
  }
}

export const giveFeedbackKey: InjectionKey<{
  onFeedbackUpdated: (
    listener: (feedback: {
      feedback: Omit<CreatableFeedback, 'feedbackReceiverId'>
      question: Question
      questionGroup: QuestionGroup
    }) => void,
  ) => void
  save: (
    route?: string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric,
  ) => Promise<void>
  updateFeedback: (newFeedback: Omit<CreatableFeedback, 'feedbackReceiverId'>) => void
  feedbacks: Ref<Omit<CreatableFeedback, 'feedbackReceiverId'>[]>
  state: Ref<'start' | 'feedback' | 'finished' | 'saved'>
  navigateToQuestionById: (question: { questionId: string; questionGroupId: string }) => void
  onNavigateToQuestionById: (
    listener: (event: { question: { questionId: string; questionGroupId: string } }) => void,
  ) => void
}> = Symbol('GiveFeedbackKey')
